import React, {
  memo,
  RefObject,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import ServicesLookup, {
  ExternalFilter,
} from 'views/EMR/components/ServiceLookup';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  filterCodes,
  normalizeServiceCodeOption,
  visitNoteFieldDirtyClasses,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import type {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  billingLookupClasses,
  serviceCodesFormKeys,
  visitNoteBillingActions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent/constants';
import type {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import type {
  QueryObserverResult,
} from '@tanstack/react-query';
import type {
  VisitNoteBillingAssessmentCodeDTO,
} from 'dtos/visitNoteBillingAssessmentCodeDTO';
import type {
  VisitNoteMipsDTO,
} from 'dtos/visitNoteMipsDTO';
import {
  getMipsSelectionsInvalidatedByCodeChange,
  removeMipsSelections,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/mipsReportingCodes';

type Props = {
  patientId: number;
  visitNoteId: number;
  sectionRef?: RefObject<HTMLDivElement>;
  favorites?: VisitNoteServiceCode[];
  refetchFavoriteCode?: () => Promise<QueryObserverResult>;
  saveVisitNote?: () => Promise<void>;
};

type SectionProps = Props & {
  field: FieldValues & { onChange: (value?: VisitNoteServiceCode[]) => void };
  isDirty: boolean;
  assessmentCodes: VisitNoteBillingAssessmentCodeDTO[];
};

function ServiceCodesSectionContent({
  field,
  isDirty,
  sectionRef,
  assessmentCodes,
  ...props
}: SectionProps) {
  const {
    control,
    setValue,
  } = useFormContext<VisitNoteData>();
  const selectedMips = useWatch({
    control,
    name: 'bill.relatedMipsList',
  }) as VisitNoteMipsDTO[] ?? [];
  const { confirm, closeConfirm } = useConfirmDialog();
  const classes = useMemo(() => ({
    ...billingLookupClasses,
    container: clsx({ [visitNoteFieldDirtyClasses]: isDirty }),
  }), [isDirty]);

  const handleServiceCodesChange = (nextServiceCodes: VisitNoteServiceCode[] = []) => {
    const currentServiceCodes = (field.value ?? []) as VisitNoteServiceCode[];
    const invalidatedMips = getMipsSelectionsInvalidatedByCodeChange(
      selectedMips,
      currentServiceCodes,
      nextServiceCodes,
    );

    if (invalidatedMips.length === 0) {
      field.onChange(nextServiceCodes);
      return;
    }

    const measureIds = invalidatedMips
      .map(({ mips }) => mips?.measureId)
      .filter(Boolean)
      .join(', ');
    confirm({
      title: 'Remove MIPS reporting code?',
      confirmText: 'Remove code and MIPS result',
      cancelText: 'Keep both',
      className: '!w-[32rem]',
      content: (
        <div className="text-sm flex flex-col gap-2">
          <p>
            This reporting code is required for MIPS Measure
            {' '}
            <span className="font-semibold">{measureIds}</span>
            . Removing it will also remove the related MIPS result.
          </p>
          <p>Are you sure you want to continue?</p>
        </div>
      ),
      onConfirm: () => {
        const nextMips = removeMipsSelections(selectedMips, invalidatedMips);
        field.onChange(nextServiceCodes);
        setValue('bill.relatedMipsList', nextMips, {
          shouldDirty: true,
          shouldTouch: true,
        });
        visitNoteEventChannel.emit('onMIPSUpdate', nextMips);
        closeConfirm();
      },
    });
  };

  return (
    <ServicesLookup
      {...field}
      onChange={handleServiceCodesChange}
      type="CPTCode"
      formKeys={serviceCodesFormKeys}
      label="Service Codes"
      dropdownId="cpt-code-selection"
      id="billingCodes"
      containerRef={sectionRef}
      allowDuplicate
      assessmentCodes={assessmentCodes}
      externalFilter={filterCodes as ExternalFilter}
      saveVisitNote={props.saveVisitNote}
      visitNoteId={props.visitNoteId}
      patientId={props.patientId}
      favorites={props.favorites ?? []}
      refetchFavoriteCode={props.refetchFavoriteCode}
      classes={classes}
      actions={visitNoteBillingActions}
      normalizeOption={normalizeServiceCodeOption}
    />
  );
}

const ServiceCodesSectionContentMemoized = memo(ServiceCodesSectionContent);

function ServiceCodesSection(props: Props) {
  const { control } = useFormContext();
  const assessmentCodes = useWatch({ control, name: 'bill.assessmentCodes' }) ?? [];

  return (
    <Controller
      name="bill.serviceCodes"
      control={control}
      render={({ field, fieldState: { isDirty } }) => (
        <ServiceCodesSectionContentMemoized
          {...props}
          field={field}
          isDirty={isDirty}
          assessmentCodes={assessmentCodes}
        />
      )}
    />
  );
}

export default memo(ServiceCodesSection);
